<template>
  <div class="app-breadcrumb" v-if="!hideBreadcrumb">
    <div class="app-breadcrumb__content">
      <ul class="app-breadcrumb__list"
        v-if="hasBreadcrumbList && !currentBreadcrumb.noNavigation">
        <li class="app-breadcrumb__item"
          v-for="(breadcrumb, key) in breadcrumbs"
          :key="breadcrumb.pageName"
        >
          <router-link
            class="app-breadcrumb__link"
            :to="{ name: breadcrumb.routeName, params: breadcrumb.params }"
            v-if="key < breadcrumbs.length - 1 && !breadcrumb.noLink"
          >
            {{ getBreadcrumbLabel(breadcrumb) }}<span class="app-breadcrumb__separator">/</span>
          </router-link>
          <span
            v-else-if="key < breadcrumbs.length - 1"
            class="app-breadcrumb__link"
          >
            {{ getBreadcrumbLabel(breadcrumb) }}<span class="app-breadcrumb__separator">/</span>
          </span>
          <span v-else>
            {{ getBreadcrumbLabel(breadcrumb) }}
          </span>
        </li>
      </ul>
      <h1 v-if="!currentBreadcrumb.noTitle" class="app-breadcrumb__page-name">
        <i
          aria-hidden="true"
          v-if="currentBreadcrumb.icon"
          :class="currentBreadcrumb.icon"
        />
        {{ getPageTitle(currentBreadcrumb) }}
      </h1>
    </div>
    <div class="app-breadcrumb__custom-content">
      <slot></slot>
    </div>
  </div>
</template>
<style lang="sass" scoped>
@import "src/styles/defaults/variables"
@import "src/styles/defaults/mobile-first"

.app-breadcrumb
  display: flex
  flex-direction: column
  align-items: flex-start
  padding: 25px 10px
  flex-shrink: 0
  +media-min-lg()
    max-height: 5.5rem
  +media-max-sm()
    align-items: center

  &__content
    display: flex
    flex-direction: column
    margin-bottom: 10px

  &__list
    display: flex
    list-style: none
    padding: 0
    margin: 0 0 2px 0
    font-size: 0.9em

  &__link
    color: $color-link
    @media print
      text-decoration: none

  &__separator
    margin: 0 8px

  &__page-name
    margin: 0
    text-transform: uppercase
    font-weight: 500
    +media-max-sm()
      text-align: center

  &__custom-content
    flex: 1
    width: 100%

  +media-min-sm()
    flex-direction: row
    align-items: center
    padding: 12px 20px
    &__content
      margin-bottom: 0
    &__custom-content
      padding-left: 40px
  +media-min-md()
    padding: 25px 40px

</style>

<script>

export default {
  methods: {
    getPageTitle(breadcrumb) {
      const meta = this.$route.meta || {};
      if (meta.pageName) {
        return meta.translatePageName ? this.$t(meta.pageName) : meta.pageName;
      }
      return this.getBreadcrumbLabel(breadcrumb);
    },
    getBreadcrumbLabel(breadcrumb) {
      if (breadcrumb.noTranslate) {
        return breadcrumb.pageName;
      }

      return this.$t(breadcrumb.pageName);
    },
    getBreadcrumbsFromRoute(route, breadcrumbs) {
      if (route.meta.breadcrumb.items) {
        return this.getBreadcrumbsFromItems(route, breadcrumbs);
      }
      breadcrumbs.push({
        pageName: route.meta.breadcrumb.name,
        routeName: route.meta.breadcrumb.routeName || route.name,
        noNavigation: route.meta.breadcrumb.noNavigation,
        noLink: route.meta.breadcrumb.noLink,
        noTranslate: route.meta.breadcrumb.noTranslate,
        noTitle: route.meta.breadcrumb.noTitle,
        icon: route.meta.breadcrumb.icon,
        params: route.params,
      });
      return breadcrumbs;
    },
    getBreadcrumbsFromItems(route, breadcrumbs) {
      const newBreadcrumbs = route.meta.breadcrumb.items.map(routeItem => ({
        ...routeItem,
        name: null,
        pageName: routeItem.name,
        routeName: routeItem.routeName || route.name,
        noTranslate: route.meta.breadcrumb.noTranslate,
        noTitle: route.meta.breadcrumb.noTitle,
        noLink: route.meta.breadcrumb.noLink,
        icon: route.meta.breadcrumb.icon,
      }));
      if (route.meta.breadcrumb.reset) {
        return newBreadcrumbs;
      }
      breadcrumbs.push(...newBreadcrumbs);
      return breadcrumbs;
    },
    applyBreadcrumbDecorator(breadcrumbs) {
      let decoratedBreadcrumbs = breadcrumbs;

      this.$emit('decorate-breadcrumb', {
        breadcrumbs,
        reset(newBreadcrumbs) {
          decoratedBreadcrumbs = newBreadcrumbs;
        },
      });

      return decoratedBreadcrumbs;
    },
  },
  computed: {
    breadcrumbs() {
      let breadcrumbs = [];
      this.$route.matched.forEach((route) => {
        if (route.meta && route.meta.breadcrumb) {
          breadcrumbs = this.getBreadcrumbsFromRoute(route, breadcrumbs);
        }
      });
      return this.applyBreadcrumbDecorator(breadcrumbs);
    },
    currentBreadcrumb() {
      return this.breadcrumbs[this.breadcrumbs.length - 1];
    },
    hasBreadcrumbList() {
      return this.breadcrumbs.length > 1;
    },
    hideBreadcrumb() {
      return !this.currentBreadcrumb
        || (
          this.currentBreadcrumb.noTitle
          && this.currentBreadcrumb.noNavigation
        );
    },
  },
};
</script>
